import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Play, DollarSign, ArrowRight, CheckCircle, Clock, Bot, FileCheck, BarChart3, Settings, Star, Users, Image as ImageIcon, ZoomIn } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { isYouTubeURL, isImageURL, getYouTubeEmbedURL } from '../../utils/mediaUtils';
import ImageModal from '../modals/ImageModal';

const AcceleratorModal = ({ accelerator, isOpen, onClose }) => {
  // Add ESC key handling
  useEscapeKey(onClose, isOpen);
  
  // State for image modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  if (!isOpen || !accelerator) return null;

  const openImageModal = () => setIsImageModalOpen(true);
  const closeImageModal = () => setIsImageModalOpen(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {/* Top Section with Request Demo Button */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-4xl font-bold text-gray-900">{accelerator.name}</h2>
                 
                  <Link
                    to="/custom"
                    className="ml-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 text-sm font-medium transition-colors"
                  >
                    <Play className="h-4 w-4" />
                    <span>Request Demo</span>
                  </Link>
                
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="bg-primary-100 text-primary-800 px-4 py-2 rounded-full font-medium">
                  {accelerator.category_name}
                </span>
                <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full font-medium">
                  {accelerator.subcategory_name}
                </span>
                {/* Pricing temporarily hidden */}
                {/* <div className="flex items-center space-x-1 text-green-600 font-semibold">
                  <span>Starting at ${accelerator.pricing_amount}/{accelerator.pricing_model?.replace('_', ' ')}</span>
                </div> */}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4">
              <X className="h-8 w-8" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Information */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-blue-900">Overview</h3>
                <p className="text-gray-700 leading-relaxed">{accelerator.description}</p>
              </div>

              {/* Input Requirements */}
              <div className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <FileCheck className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="text-xl font-semibold text-green-900">Technical Requirements</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{accelerator.input_requirements}</p>
              </div>

              {/* AI Usage */}
              <div className="bg-purple-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <Bot className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-semibold text-purple-900">Implementation Approach</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{accelerator.ai_usage}</p>
              </div>

              {/* Output */}
              <div className="bg-amber-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <BarChart3 className="h-6 w-6 text-amber-600 mr-2" />
                  <h3 className="text-xl font-semibold text-amber-900">What You Get</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{accelerator.output_provided}</p>
              </div>

              {/* Use Cases */}
              <div className="bg-indigo-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-indigo-900">Use Cases</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {accelerator.use_cases?.map((useCase, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{useCase}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customization Options */}
              <div className="bg-pink-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-pink-900">Customization Options</h3>
                <div className="space-y-2">
                  {accelerator.customization_options?.map((option, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Settings className="h-5 w-5 text-pink-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{option}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Media Demo - Video or Image */}
              
              {accelerator.video_url && (
                console.log('VIDEO_URL:', accelerator.video_url),
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
                    {isYouTubeURL(accelerator.video_url) ? (
                      <>
                        <Play className="h-6 w-6 mr-2" />
                        Demo Video
                      </>
                    ) : isImageURL(accelerator.video_url) ? (
                      <>
                        <ImageIcon className="h-6 w-6 mr-2" />
                        Preview Image
                      </>
                    ) : (
                      <>
                        <Play className="h-6 w-6 mr-2" />
                        Demo Content
                      </>
                    )}
                  </h3>
                  
                  {isYouTubeURL(accelerator.video_url) ? (
                    <div className="aspect-w-16 aspect-h-9">
                      <iframe
                        src={getYouTubeEmbedURL(accelerator.video_url)}
                        title="Accelerator Demo Video"
                        className="w-full h-64 rounded-lg"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  ) : isImageURL(accelerator.video_url) ? (
                    <div className="relative group cursor-pointer" onClick={openImageModal}>
                      <img
                        src={accelerator.video_url}
                        alt="Accelerator Preview"
                        className="w-full h-64 object-cover rounded-lg transition-transform group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Hover overlay with zoom icon */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                        <ZoomIn className="h-12 w-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                      {/* Error fallback */}
                      <div className="hidden w-full h-64 bg-gray-200 rounded-lg items-center justify-center">
                        <div className="text-center text-gray-500">
                          <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                          <p>Image unavailable</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <Play className="h-12 w-12 mx-auto mb-2" />
                        <p>Media content unavailable</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Features */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Key Features</h3>
                <div className="space-y-3">
                  {accelerator.features?.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Star className="h-4 w-4 text-yellow-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing - temporarily hidden */}
              {/* <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-green-900 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Pricing
                </h3>
                <div className="text-2xl font-bold text-green-600 mb-2">
                  Starting at ${accelerator.pricing_amount}/{accelerator.pricing_model?.replace('_', ' ')}
                </div>
                <p className="text-green-700 text-sm mb-4">Outcome-based pricing that scales with your success</p>
              </div> */}

              {/* Delivery Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-blue-900 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Delivery Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Delivery:</span>
                    <span className="font-medium">{accelerator.delivery_time}</span>
                  </div>
                </div>
              </div>

              {/* Target Tech Stacks */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-purple-900 flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Compatible Tech Stacks
                </h3>
                <div className="flex flex-wrap gap-2">
                  {accelerator.target_techstack?.map((techstack, index) => (
                    <span key={index} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                      {techstack.display_name || techstack.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                
                  <Link
                    to="/custom"
                    className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                  >
                    <Play className="h-4 w-4" />
                    <span>Request Demo</span>
                  </Link>
                
                
                <Link
                  to="/contact"
                  className="w-full border-2 border-primary-600 text-primary-600 py-3 px-4 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <span>Contact Sales</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal for full-size image viewing */}
      <ImageModal
        imageUrl={accelerator.video_url}
        alt={`${accelerator.name} Preview Image`}
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
      />
    </div>
  );
};

export default AcceleratorModal;